/**
 * URL utility functions for detecting social media platforms and content types.
 * Aligned with foodfluence-verification-dashboard urlUtils for consistent behavior.
 */
import type { DownloadedInspirationContent } from '@foodfluence/shared';

/**
 * Detects the platform from a URL
 */
export const detectPlatform = (url: string): 'instagram' | 'tiktok' | 'unknown' => {
  if (!url) return 'unknown';

  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) {
    return 'instagram';
  }

  if (lowerUrl.includes('tiktok.com') || lowerUrl.includes('vm.tiktok.com')) {
    return 'tiktok';
  }

  return 'unknown';
};

/**
 * Detects Instagram content type from URL (post, reel, or story)
 */
export const detectInstagramContentType = (url: string): 'post' | 'reel' | 'story' | 'unknown' => {
  if (!url) return 'unknown';

  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('/reel/') || lowerUrl.includes('/reels/')) {
    return 'reel';
  }

  if (lowerUrl.includes('/stories/') || lowerUrl.includes('/story/')) {
    return 'story';
  }

  if (lowerUrl.includes('/p/')) {
    return 'post';
  }

  return 'unknown';
};

/**
 * Maps a URL to its deliverable type key used in the app
 * Returns null if the URL cannot be mapped to a known deliverable type
 */
export const detectDeliverableTypeFromUrl = (url: string): 'instagramStories' | 'instagramReels' | 'instagramPosts' | 'tiktoks' | null => {
  if (!url) return null;

  const platform = detectPlatform(url);

  if (platform === 'instagram') {
    const contentType = detectInstagramContentType(url);
    switch (contentType) {
      case 'story': return 'instagramStories';
      case 'reel': return 'instagramReels';
      case 'post': return 'instagramPosts';
      default: return null;
    }
  }

  if (platform === 'tiktok') {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('/video/') || lowerUrl.includes('/t/') || lowerUrl.includes('vm.tiktok.com')) {
      return 'tiktoks';
    }
  }

  return null;
};

/**
 * One downloaded inspiration entry per link, only for links the brand still lists; a success beats
 * a failure. Overlapping download runs used to append copies of the same link (creators saw one
 * video three times), and entries outlived links the brand removed. Mirrors the backend's
 * `mergeInspirationEntries`, which now keeps stored data this way.
 */
export const mergeInspirationEntries = (
  entries: DownloadedInspirationContent[],
  links: string[],
): DownloadedInspirationContent[] => {
  const byUrl = new Map<string, DownloadedInspirationContent>();
  for (const entry of entries) {
    if (!links.includes(entry.originalUrl)) continue;
    const kept = byUrl.get(entry.originalUrl);
    if (!kept || (kept.status !== 'success' && entry.status === 'success')) byUrl.set(entry.originalUrl, entry);
  }
  return [...byUrl.values()];
};
